/**
 * Question Schema Converter
 * Handles conversion between old and new question formats
 * 
 * OLD FORMAT:
 * {
 *   question: "Text",
 *   options: ["A", "B", "C"],
 *   correctAnswerIndex: 0,
 *   allowMultiple: false
 * }
 * 
 * NEW FORMAT (Export/Import):
 * {
 *   question: "Text",
 *   options: ["A", "B", "C"],
 *   correctAnswerIndices: [0],  // or [0, 2] for multiple
 *   codeBlock: { language: "python", code: "..." },
 *   terminalOutput: "...",
 *   explanation: "Erklärung der Lösung..."
 * }
 */

/**
 * Convert from internal format to export format
 * @param {Object} question - Internal question object
 * @returns {Object} - Export-ready question object
 */
export function toExportFormat(question) {
  const exportQuestion = {
    question: question.question || '',
    options: question.options || [],
  };

  // Convert correctAnswerIndex or correctAnswerIndices to array
  if (question.correctAnswerIndices && Array.isArray(question.correctAnswerIndices)) {
    exportQuestion.correctAnswerIndices = question.correctAnswerIndices;
  } else if (question.correctAnswerIndex !== undefined) {
    exportQuestion.correctAnswerIndices = [question.correctAnswerIndex];
  } else {
    exportQuestion.correctAnswerIndices = [0];
  }

  // Add optional fields - only export if code is not empty
  if (question.codeBlock?.code) {
    exportQuestion.codeBlock = {
      language: question.codeBlock.language || 'javascript',
      code: question.codeBlock.code
    };
  }

  if (question.terminalOutput !== undefined && question.terminalOutput !== null) {
    exportQuestion.terminalOutput = question.terminalOutput;
  }

  // Add explanation if present
  if (question.explanation) {
    exportQuestion.explanation = question.explanation;
  }

  return exportQuestion;
}

/**
 * Convert from export format to internal format
 * @param {Object} question - Imported question object
 * @returns {Object} - Internal question object
 */
export function toInternalFormat(question) {
  const internalQuestion = {
    question: question.question || '',
    options: question.options || [],
  };

  // Handle correctAnswerIndices (NEW FORMAT)
  if (question.correctAnswerIndices && Array.isArray(question.correctAnswerIndices)) {
    if (question.correctAnswerIndices.length === 1) {
      // Single choice
      internalQuestion.correctAnswerIndex = question.correctAnswerIndices[0];
      internalQuestion.correctAnswerIndices = question.correctAnswerIndices;
      internalQuestion.allowMultiple = false;
    } else {
      // Multiple choice
      internalQuestion.correctAnswerIndex = question.correctAnswerIndices[0]; // Primary answer
      internalQuestion.correctAnswerIndices = question.correctAnswerIndices;
      internalQuestion.allowMultiple = true;
    }
  }
  // Handle OLD FORMAT (backward compatibility)
  else if (question.correctAnswerIndex !== undefined) {
    internalQuestion.correctAnswerIndex = question.correctAnswerIndex;
    internalQuestion.correctAnswerIndices = [question.correctAnswerIndex];
    internalQuestion.allowMultiple = question.allowMultiple || false;
  } else {
    // Default
    internalQuestion.correctAnswerIndex = 0;
    internalQuestion.correctAnswerIndices = [0];
    internalQuestion.allowMultiple = false;
  }

  // Add optional fields - only import if code is not empty
  if (question.codeBlock?.code) {
    internalQuestion.codeBlock = {
      language: question.codeBlock.language || 'javascript',
      code: question.codeBlock.code
    };
  }

  if (question.terminalOutput !== undefined && question.terminalOutput !== null) {
    internalQuestion.terminalOutput = question.terminalOutput;
  }

  // Add explanation if present
  if (question.explanation) {
    internalQuestion.explanation = question.explanation;
  }

  return internalQuestion;
}

/**
 * Convert array of questions to export format
 * @param {Array} questions - Array of internal questions
 * @returns {Array} - Array of export-ready questions
 */
export function toExportFormatArray(questions) {
  return questions.map(toExportFormat);
}

/**
 * Convert array of questions to internal format
 * @param {Array} questions - Array of imported questions
 * @returns {Array} - Array of internal questions
 */
export function toInternalFormatArray(questions) {
  return questions.map(toInternalFormat);
}

/**
 * Validate question schema
 * @param {Object} question - Question to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export function validateQuestionSchema(question) {
  const errors = [];

  if (!question.question || question.question.trim() === '') {
    errors.push('Frage-Text fehlt');
  }

  if (!question.options || !Array.isArray(question.options) || question.options.length < 2) {
    errors.push('Mindestens 2 Antwortmöglichkeiten erforderlich');
  }

  // Check if using new or old format
  if (question.correctAnswerIndices) {
    if (!Array.isArray(question.correctAnswerIndices) || question.correctAnswerIndices.length === 0) {
      errors.push('correctAnswerIndices muss ein nicht-leeres Array sein');
    } else {
      // Validate indices are within range
      const invalidIndices = question.correctAnswerIndices.filter(
        idx => idx < 0 || idx >= (question.options?.length || 0)
      );
      if (invalidIndices.length > 0) {
        errors.push(`Ungültige Antwort-Indizes: ${invalidIndices.join(', ')}`);
      }
    }
  } else if (question.correctAnswerIndex !== undefined) {
    if (question.correctAnswerIndex < 0 || question.correctAnswerIndex >= (question.options?.length || 0)) {
      errors.push('correctAnswerIndex außerhalb des gültigen Bereichs');
    }
  } else {
    errors.push('Keine richtige Antwort angegeben (correctAnswerIndices oder correctAnswerIndex fehlt)');
  }

  if (question.codeBlock) {
    if (!question.codeBlock.language) {
      errors.push('Codeblock-Sprache fehlt');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export default {
  toExportFormat,
  toInternalFormat,
  toExportFormatArray,
  toInternalFormatArray,
  validateQuestionSchema
};
